import React, { useContext, useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import { useAppContext } from "../../App";
import { Link, Divider, Drawer as MuiDrawer, IconButton, List, ListItemButton, ListItemIcon, ListItemText, Toolbar, Collapse, CircularProgress } from "@mui/material";
import styled from "@emotion/styled";
import { ChevronLeftIcon, HomeIcon } from "../icons";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import { PrivateLayoutContext } from "./PrivateLayout";
import { useTheme } from "@emotion/react";
import { roles } from "../../config/constants";
import { authorize } from "../../Core";
import { dashboardService } from "../../services/dashboard";

const drawerWidth = 240;
const menuIconSx = { minWidth: 34, alignItems: "center" };
const menuButtonSx = { minHeight: 44, px: 2, "& .MuiListItemText-primary": { fontSize: "0.9rem" } };

export const SideDrawer = ({}) => {

  const { claims, smMode, core, auth } = useAppContext();
  const [dashboards, setDashboards] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardExpanded, setDashboardExpanded] = useState(true);
  const { toggleMenu, setOpenMenu, openMenu } = useContext(PrivateLayoutContext);
  const theme = useTheme();

  const ambsActivos = useMemo(()=>{
    return Object.keys(core) 
     .filter(key => core[key].visible)
  }, [core])
  
  useEffect(()=>{
    if(smMode){
      setOpenMenu(false);
    }
  }, [smMode])

  useEffect(() => {
    if (!auth || !Object.values(core).some(item => item.visible && item["dashboard-submenus"])) {
      setDashboards([]);
      return;
    }
    const controller = new AbortController();
    setDashboardLoading(true);
    dashboardService.list({token: auth, signal: controller.signal})
      .then(data => setDashboards(data?.dashboards || []))
      .catch(error => {
        if (error?.name !== "AbortError") {
          console.warn("No se pudo cargar el menú de dashboards", error);
          setDashboards([]);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setDashboardLoading(false);
      });
    return () => controller.abort();
  }, [auth]); // Once per authenticated token, never on menu expansion.

  return (
    <Drawer variant="permanent" open={openMenu} style={{zIndex: 1000}}>
      <Toolbar
        variant="regular"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: [1],
          backgroundColor: theme.palette.background.paper
        }}
      >
        <IconButton onClick={toggleMenu}>
          <ChevronLeftIcon color={"#FFFFFF"} />
        </IconButton>
      </Toolbar>
      {authorize({rol: claims.rol, inRoles: [roles.LIQUIDADOR, roles.DIRECTOR, roles.GESTOR_AUDITOR, roles.ADMINISTRATIVO_CENTRAL, roles.GESTOR, roles.GESTOR_CENTRAL, roles.COORDINADOR, roles.COORDINADOR_CENTRAL, roles.DIRECTOR_CENTRO, roles.ADMINISTRATIVO]}) &&
        <>
          <div style={{height: 40}}></div>
          <ListItem label="Inicio" Icon={HomeIcon} href={"home"} sx={{maxHeight:40}} title={!openMenu ? 'Inicio' : '' }/>
        </>
      }
      <Divider />
      <List style={{overflowY: "auto", overflowX: "hidden", maxHeight: "calc(100vh - 190px)"}}>
      {
        ambsActivos.map(key => core[key]["dashboard-submenus"] ? (
          <React.Fragment key={key}>
            <ListItemButton onClick={() => setDashboardExpanded(value => !value)} title={!openMenu ? core[key].label : ""} sx={menuButtonSx}>
              <ListItemIcon sx={menuIconSx}>{React.createElement(core[key].icon)}</ListItemIcon>
              <ListItemText primary={core[key].label} />
              {dashboardLoading && openMenu ? <CircularProgress size={15} /> : null}
              {openMenu ? (dashboardExpanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />) : null}
            </ListItemButton>
            <Collapse in={openMenu && dashboardExpanded} timeout="auto" unmountOnExit>
              <List disablePadding>
                {dashboards.map(item => (
                  <ListItemButton key={item.id} component={CustomNavLink}
                    href={`/${key}/${item.id}/${item.slug || "dashboard"}`}
                    sx={{
                      minHeight: 38,
                      pl: 4,
                      pr: 1.5,
                      bgcolor: "action.hover",
                      borderLeft: "2px solid",
                      borderColor: "divider",
                      "& .MuiListItemText-primary": { fontSize: "0.835rem", fontWeight: 400 },
                      "&.Mui-selected": {
                        bgcolor: "action.selected",
                        borderColor: "primary.main",
                        "& .MuiListItemText-primary": { fontWeight: 600 }
                      }
                    }}>
                    <ListItemIcon sx={{ minWidth: 28, opacity: 0.75 }}><DashboardOutlinedIcon sx={{ fontSize: 18 }} /></ListItemIcon>
                    <ListItemText primary={item.nombre} />
                  </ListItemButton>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ) : (
          <ListItem key={key} label={core[key].label} Icon={core[key].icon} href={key} title={!openMenu ? core[key].label : ""}/>
        ))
      }        
      </List>

    </Drawer>
  )

}

const ListItem = ({label, Icon, ...props}) => {
  return (
    <ListItemButton href={props.href} {...props} component={CustomNavLink} sx={{ ...menuButtonSx, ...(props.sx || {}) }}>
      <ListItemIcon sx={menuIconSx}>
        <Icon />
      </ListItemIcon>
      <ListItemText primary={label} />
    </ListItemButton>
  )
}

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    '& .MuiDrawer-paper': {
      position: 'relative',
      whiteSpace: 'nowrap',
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      boxSizing: 'border-box',
      ...(!open && {
        overflowX: 'hidden',
        transition: theme.transitions.create('width', {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.leavingScreen,
        }),
        width: theme.spacing(7),
        '& .MuiListItemButton-root': {
          justifyContent: 'center',
          paddingLeft: 0,
          paddingRight: 0,
        },
        '& .MuiListItemIcon-root': {
          minWidth: 0,
          justifyContent: 'center',
        },
        '& .MuiListItemText-root': {
          display: 'none',
        },
      }),
    },
  }),
);

const CustomLink = React.forwardRef(({ href, children, ...props}, forwardedRef) => {
  return (
    <Link {...props} component={CustomNavLink} to={href} href={href} ref={forwardedRef} >
      {children}
    </Link>
  )
})

const CustomNavLink = React.forwardRef(({ href, children, ...props}, forwardedRef) => {
  return <NavLink {...props} to={href} ref={forwardedRef} className={({isActive}) => `${props.className} ${isActive ? "Mui-selected" : ""}`}>{children}</NavLink>
})
